import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { TenantSettings } from '../../../../types/tenantSettings';
import styles from './tenantSettings.module.css';
import { TENANT_SETTINGS_ENDPOINT, NUMERATION_SCHEMA_ENDPOINT } from '../../../../consts';
import { useCRUD } from '../../../../api/crud';
import axios from '../../../../api/axios';
import { basicInfoContainerStyle } from '../../../../styles/commonStyles';

const MASKED_PLACEHOLDER = '••••••••';

const SENSITIVE_KEYS = ['imap_username', 'imap_password', 'smtp_username', 'smtp_password'];

const FIELD_LABELS: Record<string, string> = {
  contracts_expiring_soon_days: 'Contracts expiring soon days',
  smtp_server: 'SMTP server',
  smtp_port: 'SMTP port',
  smtp_username: 'SMTP username',
  smtp_password: 'SMTP password',
  counter_outdated_after_days: 'Consider counter value as outdated after days',
  imap_server: 'IMAP server',
  imap_port: 'IMAP port',
  imap_username: 'IMAP username',
  imap_password: 'IMAP password',
  next_reminder_available_after_days: 'Next reminder available after days',
  reminder_template_title: 'Reminder template title',
  reminder_template_message: 'Reminder template message',
  automation_run_day: 'Settlement automation run day',
  default_job_numeration_schema: 'Default numeration schema for jobs',
  default_contract_numeration_schema: 'Default numeration schema for contracts',
  default_ordered_part_numeration_schema: 'Default numeration schema for ordered parts',
};

const TEMPLATE_FIELDS = ['reminder_template_title', 'reminder_template_message'];

const NUMERATION_SCHEMA_FIELDS: (keyof TenantSettings)[] = [
  'default_job_numeration_schema',
  'default_contract_numeration_schema',
  'default_ordered_part_numeration_schema',
];

const GROUPS: { title: string; fields: (keyof TenantSettings)[] }[] = [
  {
    title: 'Dashboard configuration',
    fields: ['contracts_expiring_soon_days'],
  },
  {
    title: 'Numeration schema defaults',
    fields: NUMERATION_SCHEMA_FIELDS,
  },
  {
    title: 'Email server configuration',
    fields: ['smtp_server', 'smtp_port', 'smtp_username', 'smtp_password'],
  },
  {
    title: 'Counters configuration',
    fields: ['counter_outdated_after_days', 'imap_server', 'imap_port', 'imap_username', 'imap_password'],
  },
  {
    title: 'Reminders configuration',
    fields: ['next_reminder_available_after_days', 'reminder_template_title', 'reminder_template_message'],
  },
  {
    title: 'Settlement automation configuration',
    fields: ['automation_run_day'],
  },
];

type DetailRowProps = {
  label: string;
  value: React.ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, value, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' },
      gap: { xs: 0.5, sm: 2 },
      alignItems: 'start',
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? 'none' : '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>{value}</Box>
  </Box>
);

function formatValue(value: unknown, masked: boolean): string {
  if (value === null || value === undefined) return '—';
  if (masked) return MASKED_PLACEHOLDER;
  if (typeof value === 'string') return value || '—';
  return String(value);
}

export const TenantSettingsInfo = () => {
  const { t } = useTranslation();
  const { get, loading } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [schemaNames, setSchemaNames] = useState<Record<string, string>>({});

  const fetchSettings = useCallback(async () => {
    const response = await get('settings');
    if (response.data) {
      setSettings(response.data);
    }
  }, [get]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    axios.get(NUMERATION_SCHEMA_ENDPOINT).then(({ data }) => {
      const schemas = Array.isArray(data?.results) ? data.results : data;
      if (Array.isArray(schemas)) {
        setSchemaNames(
          schemas.reduce((acc: Record<string, string>, schema: { id: string | number; name: string }) => {
            acc[String(schema.id)] = schema.name;
            return acc;
          }, {})
        );
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    window.addEventListener('tenantSettingsUpdated', fetchSettings);
    return () => {
      window.removeEventListener('tenantSettingsUpdated', fetchSettings);
    };
  }, [fetchSettings]);

  const toggleReveal = (key: string) => {
    setRevealed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading || !settings) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={basicInfoContainerStyle}>
      {GROUPS.map((group, groupIndex) => (
        <React.Fragment key={group.title}>
          <Box className={styles.section}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t(group.title)}
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {group.fields.map((key, index) => {
                const value = settings[key];
                const isSensitive = SENSITIVE_KEYS.includes(key);
                const isRevealed = revealed[key];
                const isNumerationSchemaField = NUMERATION_SCHEMA_FIELDS.includes(key);
                const displayValue = isNumerationSchemaField
                  ? (value !== null && value !== undefined && schemaNames[String(value)]) || '—'
                  : formatValue(value, isSensitive && !isRevealed);
                const hasValue = value !== null && value !== undefined && value !== '';
                const isTemplateField = TEMPLATE_FIELDS.includes(key);

                return (
                  <DetailRow
                    key={key}
                    label={t(FIELD_LABELS[key] ?? key)}
                    noBorder={index === group.fields.length - 1}
                    value={(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: isTemplateField ? 'flex-start' : 'center',
                          gap: 0.25,
                          width: '100%',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            whiteSpace: isTemplateField ? 'pre-wrap' : 'normal',
                            wordBreak: 'break-word',
                            width: '100%',
                          }}
                        >
                          {displayValue}
                        </Typography>
                        {isSensitive && hasValue && (
                          <IconButton
                            size="small"
                            onClick={() => toggleReveal(key)}
                            aria-label={isRevealed ? t('Hide') : t('Reveal')}
                            sx={{ flexShrink: 0, p: 0.25, ml: 0.5, mt: isTemplateField ? 0.25 : 0 }}
                          >
                            {isRevealed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        )}
                      </Box>
                    )}
                  />
                );
              })}
            </Box>
          </Box>
          {groupIndex < GROUPS.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Box>
  );
};
